import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { generatePageNumbers, PAGE_SIZE_OPTIONS, type PaginationMetadata } from '@/types/pagination';

interface PaginationControlsProps {
  pagination: PaginationMetadata;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  showPageSizeSelector?: boolean;
  /**
   * 'default' keeps the rows-per-page selector on the left and the navigation on the right.
   * 'summary' shows a "Showing X-Y of Z" statement on the left, previous/next in the centre
   * and the rows-per-page selector on the right, matching the Utilisation report layout.
   */
  layout?: 'default' | 'summary';
  /** Plural noun used by the 'summary' layout, e.g. "employees". */
  itemLabel?: string;
  className?: string;
}

export function PaginationControls({
  pagination,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  layout = 'default',
  itemLabel,
  className = '',
}: PaginationControlsProps) {
  const { currentPage, totalPages, totalItems, hasNextPage, hasPreviousPage, pageSize, startIndex, endIndex } =
    pagination;

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  if (totalItems === 0) {
    return null;
  }

  const pageSizeSelector =
    showPageSizeSelector && onPageSizeChange ? (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-muted-foreground whitespace-nowrap">Rows per page</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="h-7 w-[58px] text-xs px-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)} className="text-xs">
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : (
      <div />
    );

  const navigation = (
    <Pagination className="mx-0 w-auto">
      <PaginationContent className="gap-0.5">
        <PaginationItem>
          <PaginationPrevious
            onClick={hasPreviousPage ? () => onPageChange(currentPage - 1) : undefined}
            aria-disabled={!hasPreviousPage}
            className={`h-7 text-xs px-2 [&>span]:hidden ${!hasPreviousPage ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}
          />
        </PaginationItem>

        {pageNumbers.map((pageNum, index) => (
          <PaginationItem key={`${pageNum}-${index}`}>
            {pageNum === 'ellipsis' ? (
              <PaginationEllipsis className="size-7" />
            ) : (
              <PaginationLink
                onClick={() => onPageChange(pageNum)}
                isActive={pageNum === currentPage}
                className="h-7 w-7 text-xs cursor-pointer"
              >
                {pageNum}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            onClick={hasNextPage ? () => onPageChange(currentPage + 1) : undefined}
            aria-disabled={!hasNextPage}
            className={`h-7 text-xs px-2 [&>span]:hidden ${!hasNextPage ? 'pointer-events-none opacity-40' : 'cursor-pointer'}`}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );

  if (layout === 'summary') {
    return (
      <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 shrink-0 ${className}`}>
        <p className="text-[13px] text-muted-foreground whitespace-nowrap">
          Showing {startIndex + 1}–{endIndex} of {totalItems}{itemLabel ? ` ${itemLabel}` : ''}
        </p>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPreviousPage}
            className="h-8 text-[13px]"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Previous
          </Button>
          <span className="text-[13px] text-muted-foreground px-1 whitespace-nowrap">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNextPage}
            className="h-8 text-[13px]"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>

        {showPageSizeSelector && onPageSizeChange ? (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[13px] text-muted-foreground whitespace-nowrap">Rows per page:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
            >
              <SelectTrigger size="sm" className="w-20 text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div />
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>

      {/* Left: per-page selector */}
      {pageSizeSelector}

      {/* Centre: compact page info */}
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        Page {currentPage} of {totalPages}
      </span>

      {/* Right: prev / page numbers / next */}
      {navigation}
    </div>
  );
}
